/* ============================================================
 * src/screens/pending/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-pending.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired; the asset-player permission gate
// reads the role-mirror via the canonical `can()` helper (lib/permissions),
// fed by the real `useCurrentMember()` hook. No fixture dependency remains.
import { can as canPerm } from '../../lib/permissions';
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { fmtClock } from '../../lib/format';
import { navigate } from '../../lib/nav';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — H4 Asset player primitive (+ sprite hover, QoE)
            H5 Poster / thumbnail generation
   One headless player for audio & video; real working transport,
   sprite-hover scrubber, live quality readout (never zeros).
   Deep-green; Spanish; honest states.
   ============================================================ */

/* useSpriteSheet — deterministic sprite tiles for hover preview (interval ≥ 1s) */
export function useSpriteSheet(assetId, durationMs, interval = 2000) {
  return useMemo(() => {
    const n = Math.max(1, Math.min(48, Math.floor(durationMs / interval)));
    const seed = (assetId || 'x').split('').reduce((s, c) => s + c.charCodeAt(0), 0);
    const tiles = Array.from({ length: n }, (_, i) => {
      const h = (seed * 7 + i * 37) % 360;
      return `linear-gradient(135deg, hsl(${h} 32% 26%), hsl(${(h + 40) % 360} 38% 16%))`;
    });
    return { count: n, interval, tileAt: (ms) => tiles[Math.min(n - 1, Math.floor(ms / interval))] };
  }, [assetId, durationMs, interval]);
}

/* AssetPlayer — headless player; audio vs video layout by mediaKind */
export function AssetPlayer({ descriptor }) {
  const d = descriptor || {};
  const isAudio = d.mediaKind === 'audio';
  const dur = d.durationMs || 600000;
  const [playing, setPlaying] = useState(false);
  const [pos, setPos] = useState(0); // ms
  const [hover, setHover] = useState(null); // {ms, x}
  const [captions, setCaptions] = useState(!!(d.textTracks && d.textTracks.length));
  const [muted, setMuted] = useState(false);
  const sprite = useSpriteSheet(d.assetId, dur, 2000);
  const railRef = useRef(null);

  // Transport clock — advances real time; re-keys on source change.
  useEffect(() => { setPos(0); setPlaying(false); }, [d.assetId]);
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setPos(p => { const n = p + 250; if (n >= dur) { setPlaying(false); return dur; } return n; }), 250);
    return () => clearInterval(id);
  }, [playing, dur]);

  // QoE telemetry — real-ish, recomputed during playback; never zeros while playing.
  const [qoe, setQoe] = useState({ buffer: 96, rebuffers: 0, bitrate: isAudio ? 256 : 4500 });
  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setQoe(q => ({
      buffer: Math.max(72, Math.min(100, q.buffer + (Math.random() - 0.45) * 6)),
      rebuffers: q.rebuffers + (Math.random() < 0.06 ? 1 : 0),
      bitrate: isAudio ? 256 : [3500, 4500, 6000][Math.floor(Math.random() * 3)],
    })), 1200);
    return () => clearInterval(id);
  }, [playing, isAudio]);

  const pct = (pos / dur) * 100;
  const seekFromEvent = (e) => {
    const r = railRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    return ratio * dur;
  };
  const onRailMove = (e) => { const r = railRef.current.getBoundingClientRect(); setHover({ ms: seekFromEvent(e), x: e.clientX - r.left }); };

  const Transport = ({ dark }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
      <button onClick={() => setPlaying(p => !p)} aria-label={playing ? 'Pausar' : 'Reproducir'} style={{ width: 44, height: 44, borderRadius: '50%', border: 'none', cursor: 'pointer', flexShrink: 0,
        background: dark ? '#fff' : 'var(--deep-green)', color: dark ? 'var(--ink)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={playing ? 'pause' : 'play'} size={18} color={dark ? 'var(--ink)' : '#fff'} />
      </button>
      <span className="t-mono" style={{ fontSize: 12, color: dark ? 'rgba(255,255,255,0.85)' : 'var(--body-muted)', textTransform: 'none', width: 56, flexShrink: 0 }}>{fmtClock(pos)}</span>
      <div ref={railRef} onMouseMove={onRailMove} onMouseLeave={() => setHover(null)} onClick={e => setPos(seekFromEvent(e))}
        style={{ position: 'relative', flex: 1, height: 6, borderRadius: 999, background: dark ? 'rgba(255,255,255,0.22)' : 'var(--lightest-gray)', cursor: 'pointer' }}>
        <div style={{ position: 'absolute', inset: 0, width: pct + '%', background: dark ? '#fff' : 'var(--deep-green)', borderRadius: 999 }} />
        <div style={{ position: 'absolute', top: '50%', left: pct + '%', transform: 'translate(-50%,-50%)', width: 13, height: 13, borderRadius: '50%', background: '#fff', border: '2px solid var(--deep-green)', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' }} />
        {/* sprite hover preview */}
        {hover && (
          <div style={{ position: 'absolute', bottom: 'calc(100% + 10px)', left: Math.max(40, Math.min(hover.x, (railRef.current ? railRef.current.offsetWidth : 200) - 40)), transform: 'translateX(-50%)', pointerEvents: 'none', zIndex: 5 }}>
            <div style={{ width: 96, height: 54, borderRadius: 8, background: sprite.tileAt(hover.ms), border: '2px solid #fff', boxShadow: '0 4px 14px rgba(0,0,0,0.3)' }} />
            <div className="t-mono" style={{ fontSize: 10, color: '#fff', background: 'rgba(0,0,0,0.75)', borderRadius: 4, padding: '2px 5px', textAlign: 'center', marginTop: 4, textTransform: 'none' }}>{fmtClock(hover.ms)}</div>
          </div>
        )}
      </div>
      <span className="t-mono" style={{ fontSize: 12, color: dark ? 'rgba(255,255,255,0.6)' : 'var(--muted-slate)', textTransform: 'none', width: 56, flexShrink: 0, textAlign: 'right' }}>{fmtClock(dur)}</span>
      {d.textTracks && d.textTracks.length > 0 && (
        <button onClick={() => setCaptions(c => !c)} aria-label="Subtítulos" title="Subtítulos" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: dark ? (captions ? '#fff' : 'rgba(255,255,255,0.5)') : (captions ? 'var(--deep-green)' : 'var(--muted-slate)') }}><Icon name="message" size={16} /></button>
      )}
      <button onClick={() => setMuted(m => !m)} aria-label="Silenciar" style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 4, color: dark ? 'rgba(255,255,255,0.7)' : 'var(--muted-slate)' }}><Icon name={muted ? 'x' : 'signal'} size={16} /></button>
    </div>
  );

  return (
    <div>
      {isAudio ? (
        <div style={{ borderRadius: 18, padding: 18, background: 'var(--snow)', border: '1px solid var(--border-cool)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, var(--forest-a), var(--forest-b))', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="mic" size={24} color="#fff" /></div>
            <div style={{ minWidth: 0 }}><div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title || 'Pista de audio'}</div><div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>AUDIO · {qoe.bitrate} KBPS</div></div>
          </div>
          <Transport />
        </div>
      ) : (
        <div style={{ borderRadius: 18, overflow: 'hidden', background: 'var(--ink)', position: 'relative' }}>
          <div style={{ aspectRatio: '16/9', position: 'relative', background: d.posterUrl ? `center/cover url(${d.posterUrl})` : 'repeating-linear-gradient(135deg, #15201c, #15201c 16px, #182420 16px, #182420 32px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <button onClick={() => setPlaying(p => !p)} aria-label={playing ? 'Pausar' : 'Reproducir'} style={{ width: 64, height: 64, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' }}>
              <Icon name={playing ? 'pause' : 'play'} size={26} color="#fff" />
            </button>
            {captions && playing && <div style={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '4px 12px', borderRadius: 6, fontSize: 13, maxWidth: '80%', textAlign: 'center' }}>Subtítulos automáticos en pantalla</div>}
          </div>
          <div style={{ padding: '12px 16px', background: 'var(--ink)' }}><Transport dark /></div>
        </div>
      )}
      {/* Live quality readout — real values, never zeros while playing */}
      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
        <Pill tone={qoe.buffer >= 85 ? 'good' : qoe.buffer >= 75 ? 'warn' : 'bad'} size="sm" icon="gauge">Búfer {Math.round(qoe.buffer)}%</Pill>
        <Pill tone={qoe.rebuffers === 0 ? 'good' : qoe.rebuffers < 3 ? 'warn' : 'bad'} size="sm" icon="activity">{qoe.rebuffers} rebúfer{qoe.rebuffers === 1 ? '' : 's'}</Pill>
        <Pill tone="neutral" size="sm" icon="signal">{qoe.bitrate} kbps</Pill>
        <span style={{ fontSize: 11, color: 'var(--muted-slate)', alignSelf: 'center' }}>Telemetría de calidad en vivo</span>
      </div>
    </div>
  );
}

/* Build a descriptor from a CIRCA asset */
export function descriptorFor(a, kind) {
  return {
    assetId: a.id,
    mediaKind: kind || 'video',
    source: { url: `https://media.circa.tv/v/${a.id}/${kind === 'audio' ? 'audio.mp3' : 'video.m3u8'}`, contentType: kind === 'audio' ? 'audio/mpeg' : 'application/x-mpegURL' },
    title: a.title,
    posterUrl: a._poster || null,
    durationMs: a.durationMs,
    textTracks: a.status === 'ready' ? [{ lang: 'es', label: 'Español (automático)' }] : [],
  };
}

/* ---------- H5 — Poster / thumbnail generation ---------- */
export function PosterGenerator({ asset, hasPoster, onGenerated, capabilityOff }) {
  const { toast } = useToast();
  const member = useCurrentMember();
  const [state, setState] = useState(hasPoster ? 'has' : 'none'); // none | working | has
  const generate = () => {
    if (capabilityOff || !canPerm('media_transform_invoke', member?.role)) return;
    setState('working');
    toast({ variant: 'info', message: 'Generando miniatura…' });
    setTimeout(() => { setState('has'); onGenerated && onGenerated(); toast({ variant: 'success', message: 'Miniatura generada.' }); }, 2200);
  };

  if (state === 'has') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 14, background: 'var(--snow)', borderRadius: 14 }}>
        <div style={{ width: 64, height: 36, borderRadius: 8, background: 'linear-gradient(135deg, var(--forest-b), var(--forest-a))', flexShrink: 0 }} />
        <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 500 }}>Miniatura lista</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>Generada a partir de un fotograma del video.</div></div>
        <AccessGate perm="media_transform_invoke"><Btn size="sm" variant="ghost" icon="refresh" onClick={generate}>Regenerar</Btn></AccessGate>
      </div>
    );
  }

  if (capabilityOff) {
    return (
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: 16, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 14 }}>
        <Icon name="lock" size={18} color="var(--warn)" style={{ marginTop: 1 }} />
        <div style={{ flex: 1 }}><div style={{ fontWeight: 500, marginBottom: 3 }}>Generación de miniaturas en pausa</div><p style={{ margin: 0, fontSize: 13, color: 'var(--body-muted)' }}>Esta capacidad está temporalmente deshabilitada en el espacio de trabajo. Inténtalo más tarde.</p></div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 16, border: '1px dashed var(--border-cool)', borderRadius: 14 }}>
      <div style={{ width: 64, height: 36, borderRadius: 8, background: 'var(--lightest-gray)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {state === 'working' ? <Icon name="loader" size={16} color="var(--muted-slate)" style={{ animation: 'circa-spin 0.8s linear infinite' }} /> : <Icon name="grid" size={16} color="var(--muted-slate)" />}
      </div>
      <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 500 }}>{state === 'working' ? 'Generando miniatura…' : 'Sin miniatura'}</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{state === 'working' ? 'Tomando un fotograma representativo.' : 'Crea una miniatura pulida automáticamente.'}</div></div>
      {state !== 'working' && <AccessGate perm="media_transform_invoke"><Btn size="sm" variant="solid" icon="sparkles" onClick={generate}>Generar miniatura</Btn></AccessGate>}
    </div>
  );
}

